import React, { useCallback, useContext, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import { firedAt } from '../analytics';
import {
	useFireContentRendered,
	useFireRequestFailed,
	useFireRequestSucceeded,
} from '../hooks/useProfileCardAnalytics';
import { useUserProfileLink } from '../hooks/useUserProfileLink';
import { CardOpenTimerProvider, useCardOpenTimer } from '../store/cardOpenTimer';
import { defaultGetTestId, UserProfileCardContext } from '../store/context';
import type { HierarchyData, PersonProfileData, UserProfileCardAction } from '../types';
import { appendWorkSubpath } from '../utils/profileWorkUrl';
import { makeGetTestId } from '../utils/testId';

import { CardError } from './components/CardError';
import { CardErrorState } from './components/CardErrorState';
import { CardLoading } from './components/CardLoading';
import { DeactivatedStatus } from './components/DeactivatedStatus';
import { EmailSection } from './components/EmailSection';
import { HeaderActions } from './components/HeaderActions';
import { ReportingLinePane } from './components/hierarchy/ReportingLinePane';
import { ManagerSection } from './components/ManagerSection';
import { ProfileCardSliderPanes, ProfileCardSliderProvider } from './components/ProfileCardSlider';
import { useProfileCardSlider } from './components/ProfileCardSliderContext';
import { RecentWorkSection } from './components/RecentWorkSection';

/**
 * Props for {@link UserProfileCard} - the presentational profile card.
 */
export interface UserProfileCardProps {
	/** Profile data to render. When omitted, the card shows a loading skeleton. */
	profileData?: PersonProfileData;
	/** URLs to render as smart-link previews in the recent-work section. */
	recentWorkData?: string[];
	/**
	 * Reporting-line hierarchy for the profiled user. When present (and the user is not
	 * deactivated), the card exposes a compact control that slides from the profile/recent-work
	 * view to a reporting-line view (see {@link ProfileCardSlider}).
	 */
	hierarchyData?: HierarchyData;
	/** When `true`, renders the loading skeleton even if `profileData` is supplied. */
	isLoading?: boolean;
	/** Test id forwarded to the card root. */
	testId?: string;
	/** Site ID of the tenant the viewer is currently in. */
	cloudId: string;
	/**
	 * The error that caused profile data to be unavailable, if any. Classified into the
	 * `errorType` / `errorStatusCode` attributes on `operational.profilecard.failed.request`.
	 * When omitted and `profileData` is absent, the request is classified as `NotFound`.
	 */
	error?: unknown;
	/**
	 * Consumer-supplied custom actions exposed via the action (more-actions) menu in the
	 * top-right of the card header. Mirrors the `actions` prop on the legacy `@atlaskit/profilecard`.
	 */
	actions?: UserProfileCardAction[];
	/** Identity of the viewer. Used to suppress the (always-on) kudos entry when viewing one's own profile. */
	viewerId?: string;
}

const styles = cssMap({
	root: {
		width: '360px',
		minWidth: '340px',
		cursor: 'default',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.raised'),
	},
	deactivatedAvatar: {
		filter: 'grayscale(1)',
	},
});

/**
 * Presentational profile card displaying a user's core information and recent work.
 * Renders avatar, name, pronouns, job title, department, location, manager, and recent-work items.
 *
 * Shows a loading skeleton when `isLoading` is true or `profileData` is undefined.
 * Displays an error state when profile data is missing or `name` is not provided.
 *
 * Recent-work previews require a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope (already provided by every Atlassian product shell).
 */
export function UserProfileCard({
	profileData,
	recentWorkData,
	hierarchyData,
	isLoading = false,
	testId = 'user-profile-card',
	cloudId,
	error,
	actions,
	viewerId,
}: UserProfileCardProps): React.JSX.Element {
	const outerContext = useContext(UserProfileCardContext);
	const userId = profileData?.accountId ?? '';
	const localGetTestId = useMemo(() => makeGetTestId(userId, testId), [userId, testId]);
	const contextValue = useMemo(
		() => ({
			...outerContext,
			cloudId,
			getTestId:
				outerContext.getTestId !== defaultGetTestId ? outerContext.getTestId : localGetTestId,
		}),
		[cloudId, localGetTestId, outerContext],
	);

	const analyticsData = {
		source: 'userProfileCard' as const,
		attributes: {
			cloudId,
			userId: userId,
			hasManager: Boolean(profileData?.manager),
			hasRecentWork: Boolean(recentWorkData?.length),
			hasThirdPartyIntegrations: Boolean(profileData?.thirdPartyIntegrations?.length),
		},
	};

	return (
		<TeamsAppAnalyticsContext data={analyticsData}>
			<UserProfileCardContext.Provider value={contextValue}>
				<CardOpenTimerProvider>
					<UserProfileCardErrorBoundary testId={testId}>
						<UserProfileCardContent
							profileData={profileData}
							recentWorkData={recentWorkData}
							hierarchyData={hierarchyData}
							isLoading={isLoading}
							testId={testId}
							cloudId={cloudId}
							error={error}
							actions={actions}
							viewerId={viewerId}
						/>
					</UserProfileCardErrorBoundary>
				</CardOpenTimerProvider>
			</UserProfileCardContext.Provider>
		</TeamsAppAnalyticsContext>
	);
}

function UserProfileCardErrorBoundary({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const handleError = useCallback(
		(_error: Error) => {
			// `default` (not `renderError`) - schema only allows `default | NotFound`.
			fireEvent('ui.profilecard.rendered.error', {
				firedAt: firedAt(),
				errorType: 'default',
				hasRetry: false,
			});
		},
		[fireEvent],
	);

	return (
		<ErrorBoundary onError={handleError} fallback={<CardError testId={testId} />}>
			{children}
		</ErrorBoundary>
	);
}

function UserProfileCardContent({
	profileData,
	recentWorkData,
	hierarchyData,
	isLoading,
	testId,
	cloudId,
	error,
	actions,
	viewerId,
}: Required<Pick<UserProfileCardProps, 'isLoading' | 'testId' | 'cloudId'>> &
	Pick<
		UserProfileCardProps,
		'profileData' | 'recentWorkData' | 'hierarchyData' | 'error' | 'actions' | 'viewerId'
	>): React.JSX.Element {
	const { getTestId } = useContext(UserProfileCardContext);

	const hasProfileData = Boolean(profileData && profileData.name);

	useFireRequestSucceeded(!isLoading && hasProfileData);
	useFireRequestFailed(!isLoading && !hasProfileData ? (error ?? null) : undefined);

	if (isLoading) {
		return <CardLoading testId={testId} />;
	}

	if (!profileData || !profileData.name) {
		return <CardErrorState testId={testId} errorType="NotFound" />;
	}

	return (
		<UserProfileCardRenderedContent
			profileData={profileData}
			recentWorkData={recentWorkData}
			hierarchyData={hierarchyData}
			testId={testId}
			cloudId={cloudId}
			getTestId={getTestId}
			actions={actions}
			viewerId={viewerId}
		/>
	);
}

/**
 * Success-only render, split out so `useFireContentRendered` fires only when the content is shown
 * (not on the loading or error states).
 */
function UserProfileCardRenderedContent({
	profileData,
	recentWorkData,
	hierarchyData,
	testId,
	cloudId,
	getTestId,
	actions,
	viewerId,
}: {
	profileData: PersonProfileData;
	recentWorkData?: string[];
	hierarchyData?: HierarchyData;
	testId: string;
	cloudId: string;
	getTestId: (suffix: string) => string | undefined;
	actions?: UserProfileCardAction[];
	viewerId?: string;
}): React.JSX.Element {
	useFireContentRendered(profileData);
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const { townsquareApp } = useContext(UserProfileCardContext);
	const {
		href: profileHref,
		target: profileTarget,
		onNavigate: onProfileNavigate,
	} = useUserProfileLink(profileData.accountId, cloudId, townsquareApp);
	const viewAllUrl = appendWorkSubpath(profileHref);

	const handleRecentWorkViewAllClick = useCallback(() => {
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: Boolean(viewAllUrl),
			hasOnClick: true,
			index: 0,
			actionId: 'recentWorkViewAll',
		});
	}, [fireEvent, getDurationSinceInteraction, viewAllUrl]);

	const handleViewReportingLine = useCallback(() => {
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: false,
			hasOnClick: true,
			index: 1,
			actionId: 'viewReportingLine',
		});
	}, [fireEvent, getDurationSinceInteraction]);

	const handleBackToProfile = useCallback(() => {
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: false,
			hasOnClick: true,
			index: 0,
			actionId: 'backToProfile',
		});
	}, [fireEvent, getDurationSinceInteraction]);

	const handleViewFullReportingLineClick = useCallback(() => {
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: Boolean(profileHref),
			hasOnClick: true,
			index: 0,
			actionId: 'viewFullReportingLine',
		});
	}, [fireEvent, getDurationSinceInteraction, profileHref]);

	const handleRecentWorkLinkClick = useCallback(
		(url: string, index: number) => {
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref: Boolean(url),
				hasOnClick: true,
				index,
				actionId: 'recentWorkLink',
			});
		},
		[fireEvent, getDurationSinceInteraction],
	);

	const { name, pronouns, avatarUrl, jobTitle, department, location, localTime, manager, email } =
		profileData;

	// Deactivated (inactive/closed) accounts get a reduced experience: greyed-out avatar, only the
	// "View profile" action, a status lozenge, and no manager / recent-work sections.
	const isDeactivated =
		profileData.accountStatus === 'inactive' || profileData.accountStatus === 'closed';

	const subtitleParts = [jobTitle, department].filter(Boolean);
	const subtitle = subtitleParts.join(` \u2022 `);

	const locationLine =
		localTime && location ? `${location} \u2013 ${localTime}` : (location ?? undefined);

	const hasRecentWork = Boolean(recentWorkData?.length);
	const hasHierarchy = Boolean(hierarchyData?.user?.accountId);
	const useSlider = !isDeactivated && hasHierarchy;

	const recentWorkPanel =
		hasRecentWork && recentWorkData ? (
			<RecentWorkSection
				recentWorkData={recentWorkData}
				testId={getTestId('recent-work')}
				viewAllUrl={viewAllUrl}
				onViewAllLinkClick={handleRecentWorkViewAllClick}
				onLinkClick={handleRecentWorkLinkClick}
			/>
		) : null;

	const profileHeader = (
		<Stack space="space.075">
			<Inline spread="space-between" alignBlock="start">
				<Box xcss={isDeactivated ? styles.deactivatedAvatar : undefined}>
					<Avatar
						src={avatarUrl ?? undefined}
						name={name}
						size="xlarge"
						testId={getTestId('avatar')}
					/>
				</Box>
				<HeaderActions
					profileData={profileData}
					cloudId={cloudId}
					actions={actions}
					viewerId={viewerId}
					isDeactivated={isDeactivated}
				/>
			</Inline>

			<Stack space="space.050">
				<Inline space="space.100" alignBlock="end">
					<Heading as="h2" size="medium" testId={getTestId('name')}>
						{name}
					</Heading>
					{pronouns && (
						<Text color="color.text.subtlest" testId={getTestId('pronouns')}>
							{pronouns}
						</Text>
					)}
				</Inline>
				{subtitle && (
					<Text maxLines={1} color="color.text.subtlest" testId={getTestId('subtitle')}>
						{subtitle}
					</Text>
				)}
				{locationLine && (
					<Text maxLines={1} color="color.text.subtlest" testId={getTestId('location')}>
						{locationLine}
					</Text>
				)}
				{!isDeactivated && email && <EmailSection email={email} />}
				{isDeactivated && (
					<Inline>
						<DeactivatedStatus />
					</Inline>
				)}
			</Stack>
		</Stack>
	);

	const managerSection = !isDeactivated && manager ? <ManagerSection manager={manager} /> : null;

	const mainColumn = (
		<Stack space="space.150">
			{profileHeader}
			{managerSection}
			{!isDeactivated && recentWorkPanel}
		</Stack>
	);

	const content = (
		<Box xcss={styles.root} testId={testId} role="region" aria-label={name}>
			<Stack space="space.150" testId="profilecard">
				{useSlider ? (
					<ReportingLineSliderBody
						front={mainColumn}
						renderBack={({ back, headingRef, resetToken }) => (
							<ReportingLinePane
								hierarchyData={hierarchyData}
								viewFullUrl={profileHref}
								target={profileTarget}
								onNavigate={onProfileNavigate}
								onViewFullClick={handleViewFullReportingLineClick}
								onBack={back}
								headingRef={headingRef}
								resetToken={resetToken}
								testId={getTestId('reporting-line')}
							/>
						)}
						testId={getTestId('slider')}
					/>
				) : (
					mainColumn
				)}
			</Stack>
		</Box>
	);

	if (!useSlider) {
		return content;
	}

	return (
		<ProfileCardSliderProvider onView={handleViewReportingLine} onBack={handleBackToProfile}>
			{content}
		</ProfileCardSliderProvider>
	);
}

/**
 * Renders the two-pane reporting-line viewport, reading the shared slide state (toggled by the
 * trigger on the Manager row) from {@link ProfileCardSliderContext}.
 */
function ReportingLineSliderBody({
	front,
	renderBack,
	testId,
}: {
	front: React.ReactNode;
	renderBack: (args: {
		back: () => void;
		headingRef: React.RefObject<HTMLDivElement>;
		/** Increments each time the pane opens, so the list re-centers on the profiled user. */
		resetToken: number;
	}) => React.ReactNode;
	testId?: string;
}): React.JSX.Element | null {
	const slider = useProfileCardSlider();
	if (!slider) {
		return null;
	}
	return (
		<ProfileCardSliderPanes
			front={front}
			back={({ back, headingRef }) =>
				renderBack({ back, headingRef, resetToken: slider.resetToken })
			}
			showBack={slider.showBack}
			onBack={slider.onBack}
			backPaneId={slider.backPaneId}
			testId={testId}
		/>
	);
}
